import { Filter, FilterOption } from "@/components/DesignSystem/Toolbar/interfaces";
import { QueryKeys } from '@/constants/QueryKeys';
import { useAuthentication } from "@/contexts/dataSync/AuthenticationContext";
import { RetailerApi } from "@/utilities/api/RetailerApi";
import { RetailerLocationApi } from "@/utilities/api/RetailerLocationApi";
import { SharedConfig } from "@shared/SharedConfig";
import { Resolved } from "@shared/utilities/UtilityTypes";
import { useQuery } from 'react-query';

export const useRetailerLocationFilter = (retailerId?: string): Filter => {
  const { retailer, isInternal } = useAuthentication();

  const retailerIdFilter = retailer?.id ?? retailerId;
  const { data: locations } = useQuery(
    [
      retailerIdFilter ? QueryKeys.GET_RETAILER_LOCATION_LIST : QueryKeys.GET_LOCATIONS,
      retailerIdFilter,
    ],
    async () => {
      if (retailerIdFilter) {
        return RetailerApi.getLocations(retailerIdFilter, {
          limit: SharedConfig.maxPageLimit,
        });
      } else if (isInternal) {
        return RetailerLocationApi.list({
          isActive: true,
          limit: SharedConfig.maxPageLimit,
        });
      }
    },
    { staleTime: 5 * 60 * 1000 },
  );

  if (retailerIdFilter) {
    return {
      id: 'locations',
      label: 'Location',
      options: locations?.data.map((location) => ({
        id: location.id,
        label: location.name,
      })) ?? [],
      selectionMethod: 'multi-select',
    };
  }

  const filterOptionsMap:
    Map<string, Resolved<FilterOption, 'subOptionLabel' | 'subOptions'>> = new Map();

  locations?.data.forEach(({ id, name, retailer }) => {
    if (!retailer) {
      return;
    }
    if (filterOptionsMap.has(retailer.id)) {
      filterOptionsMap.get(retailer.id)?.subOptions.push({
        id,
        label: name,
      });
    } else {
      filterOptionsMap.set(retailer.id, {
        id: retailer.id,
        label: retailer.name,
        subOptionLabel: 'Locations',
        subOptions: [{
          id,
          label: name,
        }],
      });
    }
  });

  return {
    id: 'locations',
    label: 'Retailer & Locations',
    options: [...filterOptionsMap.values()]
      .sort((a, b) => a.label.localeCompare(b.label)),
    selectionMethod: 'multi-select',
  };
};
